import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal, NgbDropdownModule, NgbModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { CompliancesLibraryService } from '../../../services/compliances-library.service';
import { ComplianceOverviewService } from '../../../../compliance-overview/services/compliance-overview.service';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { ConfirmationModalService } from '../../../../../../_shared/services/modals/confirmation-modal.service';
import { ComplianceRegion } from '../../../../compliance-overview/models/compliance-item.model';
import { AccountFilterComponent } from '../../../../../../_shared/components/filters/account-filter/account-filter.component';
import { AccountFilterData } from '../../../../../../_shared/models';
import { CHECKLIST_TYPE_NAME_KEYS, CHECKLIST_TYPES, ChecklistTypeIdEnum } from '../../../../_shared/models/checklist-type.model';
import { LinkDocumentTypeModalComponent } from '../../../../_shared/components/modals/link-document-type-modal/link-document-type-modal.component';
import { LinkDocumentTypeResult, LinkedDocumentTypeUsage } from '../../../../_shared/models/link-document-type.model';
import { COMPLIANCE_TYPE_NAME_KEYS, COMPLIANCE_TYPES, ComplianceFrequency, ComplianceTypeEnum, CreateCustomComplianceRequest, CustomComplianceChecklist, CustomComplianceRequirement, RoutineType } from '../../../models/compliances.model';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { LookupLabelPipe } from '../../../../../../_shared/pipes/lookup-label.pipe';
import { translateComplianceFrequency, translateRoutineTypeName } from '../../../../_shared/helpers/compliance-lookup-label.helper';

@Component({
  selector: 'app-compliance-create-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgbDropdownModule,
    NgbTooltipModule,
    AccountFilterComponent,
    TranslocoDirective,
    TranslocoPipe,
    LookupLabelPipe,
  ],
  templateUrl: './compliance-create-modal.component.html',
  styleUrl: './compliance-create-modal.component.scss'
})
export class ComplianceCreateModalComponent implements OnInit, OnDestroy {
  private activeModal = inject(NgbActiveModal);
  private modalService = inject(NgbModal);
  private confirmationModalService = inject(ConfirmationModalService);
  private libraryService = inject(CompliancesLibraryService);
  private overviewService = inject(ComplianceOverviewService);
  private errorService = inject(ErrorHandlerService);
  private transloco = inject(TranslocoService);
  private cancellation = new Subject<void>();

  accountFilter: AccountFilterData = { accountIds: [] };
  selectedAccountIds: number[] = [];
  complianceName = '';
  selectedRegions: ComplianceRegion[] = [];
  selectedComplianceTypeId: number | null = null;
  selectedFrequencyId: number | null = null;
  selectedRoutineTypeId: number | null = null;

  checklistName = '';
  selectedChecklistTypeId: number | null = null;
  requirements: CustomComplianceRequirement[] = [];
  editingIndex: number | null = null;
  checklistNameError: string | null = null;

  checklists: CustomComplianceChecklist[] = [];

  regions: ComplianceRegion[] = [];
  regionSearchTerm = '';
  isLoadingRegions = true;
  isSaving = false;

  get filteredRegions(): ComplianceRegion[] {
    const term = this.regionSearchTerm.trim().toLowerCase();
    return term ? this.regions.filter(r => r.name.toLowerCase().includes(term)) : this.regions;
  }

  readonly complianceTypes = COMPLIANCE_TYPES;
  readonly checklistTypes = CHECKLIST_TYPES;
  readonly ComplianceTypeEnum = ComplianceTypeEnum;

  frequencies: ComplianceFrequency[] = [];
  routineTypes: RoutineType[] = [];
  isLoadingFrequencies = false;
  isLoadingRoutineTypes = false;

  ngOnInit(): void {
    this.loadRegionsAsync();
    this.loadFrequenciesAsync();
    this.loadRoutineTypesAsync();
  }

  ngOnDestroy(): void {
    this.cancellation.next();
    this.cancellation.complete();
  }

  private async loadFrequenciesAsync(): Promise<void> {
    this.isLoadingFrequencies = true;
    try {
      const response = await this.libraryService.getComplianceFrequenciesAsync(this.cancellation);
      this.frequencies = response.data ?? [];
      this.isLoadingFrequencies = false;
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isLoadingFrequencies = false;
        }
      });
    }
  }

  private async loadRoutineTypesAsync(): Promise<void> {
    this.isLoadingRoutineTypes = true;
    try {
      this.routineTypes = await this.getRoutineTypesForSelectedAccounts();

      // Drop a selection that is no longer offered for the chosen accounts
      if (this.selectedRoutineTypeId !== null && !this.routineTypes.some(r => r.id === this.selectedRoutineTypeId)) {
        this.selectedRoutineTypeId = null;
      }

      this.isLoadingRoutineTypes = false;
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isLoadingRoutineTypes = false;
        }
      });
    }
  }

  private async getRoutineTypesForSelectedAccounts(): Promise<RoutineType[]> {
    if (this.selectedAccountIds.length === 0) {
      const response = await this.libraryService.getRoutineTypesAsync(this.cancellation);
      return response.data ?? [];
    }

    const responses = await Promise.all(
      this.selectedAccountIds.map(accountId =>
        this.libraryService.getRoutineTypesByAccountAsync(accountId, this.cancellation))
    );

    const [first, ...rest] = responses.map(r => r.data ?? []);
    return first.filter(type => rest.every(list => list.some(t => t.id === type.id)));
  }

  private async loadRegionsAsync(): Promise<void> {
    try {
      const response = await this.overviewService.getComplianceRegionsAsync(this.cancellation);
      this.regions = response.data ?? [];
      this.isLoadingRegions = false;
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isLoadingRegions = false;
        }
      });
    }
  }

  onAccountFilterChange(filter: AccountFilterData): void {
    this.selectedAccountIds = filter.accountIds;
    void this.loadRoutineTypesAsync();
  }

  toggleRegion(region: ComplianceRegion): void {
    const idx = this.selectedRegions.findIndex(r => r.id === region.id && r.type === region.type);
    if (idx >= 0) {
      this.selectedRegions.splice(idx, 1);
    } else {
      this.selectedRegions.push(region);
    }
  }

  isRegionSelected(region: ComplianceRegion): boolean {
    return this.selectedRegions.some(r => r.id === region.id && r.type === region.type);
  }

  get regionsLabel(): string {
    const count = this.selectedRegions.length;
    if (!count) {
      return this.transloco.translate('compliance.library.createModal.selectRegions');
    }
    return this.transloco.translate(
      count === 1
        ? 'compliance.library.createModal.regionsSelectedCountOne'
        : 'compliance.library.createModal.regionsSelectedCountMany',
      { count }
    );
  }

  get complianceTypeLabel(): string {
    const selected = this.complianceTypes.find(type => type.id === this.selectedComplianceTypeId);
    return selected
      ? this.complianceTypeName(selected.id)
      : this.transloco.translate('compliance.library.createModal.selectComplianceType');
  }

  complianceTypeName(id: ComplianceTypeEnum): string {
    return this.transloco.translate(COMPLIANCE_TYPE_NAME_KEYS[id]);
  }

  checklistTypeName(id: ChecklistTypeIdEnum): string {
    return this.transloco.translate(CHECKLIST_TYPE_NAME_KEYS[id]);
  }

  get checklistTypeLabel(): string {
    const selected = this.checklistTypes.find(type => type.id === this.selectedChecklistTypeId);
    return selected
      ? this.checklistTypeName(selected.id)
      : this.transloco.translate('compliance.library.createModal.selectChecklistType');
  }

  /** Inspection requirements cannot link document types, so the link button is hidden. */
  get isInspectionChecklistSelected(): boolean {
    return this.selectedChecklistTypeId === ChecklistTypeIdEnum.Inspection;
  }

  get selectedFrequencyName(): string {
    const selected = this.frequencies.find(f => f.id === this.selectedFrequencyId);
    return selected
      ? translateComplianceFrequency(this.transloco, selected.name)
      : this.transloco.translate('compliance.library.createModal.selectFrequency');
  }

  get selectedRoutineTypeName(): string {
    const selected = this.routineTypes.find(r => r.id === this.selectedRoutineTypeId);
    return selected
      ? translateRoutineTypeName(this.transloco, selected.name)
      : this.transloco.translate('compliance.library.createModal.selectType');
  }

  get hasSelectedComplianceSchedule(): boolean {
    if (this.selectedComplianceTypeId === ComplianceTypeEnum.Periodic) {
      return this.selectedFrequencyId !== null;
    }

    if (this.selectedComplianceTypeId === ComplianceTypeEnum.Routine) {
      return this.selectedRoutineTypeId !== null;
    }

    return false;
  }

  get fieldLabel(): string {
    if (this.selectedComplianceTypeId === ComplianceTypeEnum.Periodic) return this.transloco.translate('compliance.library.createModal.frequency');
    if (this.selectedComplianceTypeId === ComplianceTypeEnum.Routine) return this.transloco.translate('compliance.library.createModal.routineType');
    return this.transloco.translate('compliance.library.createModal.frequencyOrRoutineType');
  }

  get dropdownLabel(): string {
    if (this.selectedComplianceTypeId === ComplianceTypeEnum.Periodic) return this.selectedFrequencyName;
    if (this.selectedComplianceTypeId === ComplianceTypeEnum.Routine) return this.selectedRoutineTypeName;
    return this.transloco.translate('compliance.library.createModal.selectComplianceTypeFirst');
  }

  get isDropdownDisabled(): boolean {
    return this.selectedComplianceTypeId === null;
  }

  get isDropdownPlaceholder(): boolean {
    if (this.selectedComplianceTypeId === ComplianceTypeEnum.Periodic) return this.selectedFrequencyId === null;
    if (this.selectedComplianceTypeId === ComplianceTypeEnum.Routine) return this.selectedRoutineTypeId === null;
    return true;
  }

  selectComplianceType(type: typeof COMPLIANCE_TYPES[number]): void {
    this.selectedComplianceTypeId = type.id;
    this.selectedFrequencyId = null;
    this.selectedRoutineTypeId = null;
  }

  selectChecklistType(type: typeof CHECKLIST_TYPES[number]): void {
    this.selectedChecklistTypeId = type.id;
    if (this.isInspectionChecklistSelected) {
      this.requirements.forEach(requirement => {
        requirement.documentTypeId = null;
        requirement.documentTypeName = null;
      });
    }
    this.validateChecklistCategory();
  }

  addRequirement(): void {
    this.requirements.push({ name: '', documentTypeId: null, documentTypeName: null, critical: false });
  }

  removeRequirement(index: number): void {
    this.requirements.splice(index, 1);
  }

  openLinkDocumentTypeModal(row: CustomComplianceRequirement): void {
    const modalRef = this.modalService.open(LinkDocumentTypeModalComponent, {
      centered: true,
      windowClass: 'link-document-type-modal-window'
    });
    modalRef.componentInstance.requirementName = row.name;
    modalRef.componentInstance.usedTypes = this.collectUsedTypes(row);

    modalRef.result.then(
      (result: LinkDocumentTypeResult) => {
        row.documentTypeId = result.documentTypeId;
        row.documentTypeName = result.documentTypeName;
      },
      () => {
        // Dismissed.
      }
    );
  }

  confirmUnlinkDocumentType(row: CustomComplianceRequirement): void {
    void this.confirmationModalService.showConfirmationModal({
      title: this.transloco.translate('compliance.library.createModal.confirmations.unlink.title'),
      message: this.transloco.translate('compliance.library.createModal.confirmations.unlink.message'),
      warningMessage: this.transloco.translate('compliance.library.createModal.confirmations.unlink.warning'),
      confirmText: this.transloco.translate('compliance.library.createModal.confirmations.unlink.confirm'),
      declineText: this.transloco.translate('compliance.library.createModal.confirmations.unlink.decline'),
      onConfirm: async () => {
        row.documentTypeId = null;
        row.documentTypeName = null;
      }
    });
  }

  private collectUsedTypes(current: CustomComplianceRequirement): LinkedDocumentTypeUsage[] {
    const cardRequirements = this.checklists
      .filter((_, index) => index !== this.editingIndex)
      .flatMap(checklist => checklist.requirements);
    const formRequirements = this.requirements.filter(requirement => requirement !== current);

    return [...cardRequirements, ...formRequirements]
      .filter(requirement => requirement.documentTypeId !== null && requirement.documentTypeId !== undefined)
      .map(requirement => ({
        documentTypeId: requirement.documentTypeId!,
        requirementName: requirement.name.trim() || this.transloco.translate('compliance.library.createModal.anotherRequirement')
      }));
  }

  addChecklist(): void {
    if (!this.checklistName.trim() || !this.selectedChecklistTypeId) return;

    this.validateChecklistCategory();
    if (this.checklistNameError) return;

    const checklist: CustomComplianceChecklist = {
      name: this.checklistName.trim(),
      checklistTypeId: this.selectedChecklistTypeId,
      requirements: this.requirements
        .filter(requirement => requirement.name.trim())
        .map(requirement => ({ ...requirement, name: requirement.name.trim() })),
    };
    if (this.editingIndex !== null) {
      this.checklists[this.editingIndex] = checklist;
      this.editingIndex = null;
    } else {
      this.checklists.push(checklist);
    }
    this.resetChecklistForm();
  }

  editChecklist(index: number): void {
    const c = this.checklists[index];
    this.checklistName = c.name;
    this.selectedChecklistTypeId = c.checklistTypeId;
    this.requirements = c.requirements.map(requirement => ({ ...requirement }));
    this.editingIndex = index;
    this.checklistNameError = null;
  }

  deleteChecklist(index: number): void {
    this.checklists.splice(index, 1);
    if (this.editingIndex === index) {
      this.editingIndex = null;
      this.resetChecklistForm();
    }
  }

  private resetChecklistForm(): void {
    this.checklistName = '';
    this.selectedChecklistTypeId = null;
    this.requirements = [];
    this.checklistNameError = null;
  }

  onChecklistNameChanged(value: string): void {
    this.checklistName = value;
    this.validateChecklistCategory();
  }

  private validateChecklistCategory(): void {
    if (!this.checklistName.trim() || !this.selectedChecklistTypeId) {
      this.checklistNameError = null;
      return;
    }

    this.checklistNameError = this.hasDuplicateChecklistCategory()
      ? this.transloco.translate('compliance.library.createModal.duplicateCategory')
      : null;
  }

  private hasDuplicateChecklistCategory(): boolean {
    const categoryName = this.normalizeCategoryName(this.checklistName);

    return this.checklists.some((checklist, index) =>
      index !== this.editingIndex
      && checklist.checklistTypeId === this.selectedChecklistTypeId
      && this.normalizeCategoryName(checklist.name) === categoryName
    );
  }

  private normalizeCategoryName(value: string): string {
    return value.trim().replace(/\s+/g, ' ').toLowerCase();
  }

  get isFormValid(): boolean {
    return (
      this.complianceName.trim().length > 0 &&
      this.selectedAccountIds.length > 0 &&
      this.selectedComplianceTypeId !== null &&
      this.hasSelectedComplianceSchedule &&
      this.checklists.length > 0
    );
  }

  async saveAsync(): Promise<void> {
    if (!this.isFormValid || this.isSaving) return;
    this.isSaving = true;
    try {
      const request: CreateCustomComplianceRequest = {
        accountIds: this.selectedAccountIds,
        name: this.complianceName.trim(),
        regionIds: this.selectedRegions.filter(r => r.type === 'region').map(r => r.id),
        countryIds: this.selectedRegions.filter(r => r.type === 'country').map(r => r.id),
        complianceTypeId: this.selectedComplianceTypeId!,
        frequencyId: this.selectedFrequencyId ?? undefined,
        routineTypeId: this.selectedRoutineTypeId ?? undefined,
        checklists: this.checklists.map(checklist => ({
          name: checklist.name,
          checklistTypeId: checklist.checklistTypeId,
          requirements: checklist.requirements.map(requirement => ({
            name: requirement.name,
            documentTypeId: requirement.documentTypeId,
            isCritical: requirement.critical ?? false
          }))
        })),
      };
      await this.libraryService.createCustomComplianceAsync(request);
      this.isSaving = false;
      this.activeModal.close(true);
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isSaving = false;
        }
      });
    }
  }

  close(): void {
    this.activeModal.dismiss();
  }
}